/**
 * Sales representatives, and the reassignment that has to be atomic.
 *
 * Moving a portfolio is one statement on the server, and this screen is where
 * somebody triggers it. A representative can hold several hundred customers;
 * doing it row by row would leave the portfolio half-moved if anything failed
 * in the middle, and nobody would know which half.
 *
 * "Credit followed" is the sum of what this representative's customers owe. It
 * is on this screen rather than only in the portfolio because it is the figure
 * a sales manager compares between representatives.
 *
 * Each commercial also carries the customer sheet's « Gestion commerciale »: the type of
 * customer they handle and their qualification, both picked from lists an administrator keeps
 * here, and the companies they work for. The whole list goes out and comes back as one file,
 * the same columns both ways.
 *
 * A commercial is created once, here: given an address, the sheet opens their login with the
 * Commercial profile and sends the invitation. Logins someone gave that profile in Administration
 * before are announced at the top, to be linked to their sheet once.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import { ApiError, type ApiClient } from "@hardauto/core-api";
import { download, useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { money } from "@hardauto/core-i18n";
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Dialog,
  Input,
  MultiSelect,
  Page,
  Select,
  StatusDot,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import {
  ClientsApi,
  type SalesRep,
  type SalesRepImportReport,
  type SalesRepOption,
  type SalesRepOptionKind,
} from "../api";
import { NAMESPACE } from "../i18n";
import { CLIENTS_PATHS } from "../paths";
import { ListsDialog } from "./salesReps/ListsDialog";
import { SalesRepDialog, type SalesRepChanges } from "./salesReps/SalesRepDialog";
import "./clients.css";

export function SalesRepsScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ClientsApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("clients.rep.write");
  const mayCreateAccounts = useCan("users.account.create");
  const mayGrantProfiles = useCan("access.assignment.manage");
  const mayGiveLogin = mayWrite && mayCreateAccounts && mayGrantProfiles;
  const mayAssign = useCan("clients.client.write");

  const fetcher = useCallback(() => api.listSalesReps(), [api]);
  const reps = useResource(fetcher);
  const optionsFetcher = useCallback(() => api.salesRepOptions(), [api]);
  const options = useResource(optionsFetcher);
  const unlinkedFetcher = useCallback(
    () => (mayGiveLogin ? api.unlinkedSalesRepAccounts() : Promise.resolve({ items: [] })),
    [api, mayGiveLogin],
  );
  const unlinked = useResource(unlinkedFetcher);
  const companiesFetcher = useCallback(() => api.salesRepCompanies(), [api]);
  const companies = useResource(companiesFetcher);
  const companyName = useMemo(
    () => new Map((companies.data?.items ?? []).map((company) => [company.code, company.name])),
    [companies.data],
  );

  /** The values of one list, as the create form and the sheet offer them. */
  const listed = (kind: SalesRepOptionKind) =>
    (options.data?.items ?? [])
      .filter((option) => option.kind === kind)
      .map((option) => option.label);

  const [editing, setEditing] = useState<SalesRep | null>(null);
  const [showLists, setShowLists] = useState(false);
  const [report, setReport] = useState<SalesRepImportReport | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [territory, setTerritory] = useState("");
  const [clientType, setClientType] = useState("");
  const [qualification, setQualification] = useState("");
  const [companyCodes, setCompanyCodes] = useState<string[]>([]);

  const [reassigning, setReassigning] = useState(false);
  const [fromCode, setFromCode] = useState("");
  const [toCode, setToCode] = useState("");

  const available = reps.data?.items ?? [];

  const columns: Column<SalesRep>[] = [
    { key: "code", header: t("salesReps.column.code"), render: (row) => <code>{row.code}</code> },
    {
      key: "name",
      header: t("salesReps.column.name"),
      render: (row) => (
        <Link to={CLIENTS_PATHS.clients} search={{ rep: row.code }}>
          {row.name}
        </Link>
      ),
    },
    {
      key: "territory",
      header: t("salesReps.column.territory"),
      render: (row) => row.territory || "—",
    },
    {
      key: "sites",
      header: t("salesReps.column.sites"),
      render: (row) =>
        row.site_codes.length === 0 ? (
          <span className="hardauto-cell__muted">{t("salesReps.none")}</span>
        ) : (
          row.site_codes.join(", ")
        ),
    },
    {
      key: "clientType",
      header: t("salesReps.column.clientType"),
      render: (row) => row.client_type || "—",
    },
    {
      key: "qualification",
      header: t("salesReps.column.qualification"),
      render: (row) => row.qualification || "—",
    },
    {
      key: "companies",
      header: t("salesReps.column.companies"),
      render: (row) =>
        row.company_codes.length === 0 ? (
          <span className="hardauto-cell__muted">{t("salesReps.none")}</span>
        ) : (
          row.company_codes.map((code) => companyName.get(code) || code).join(", ")
        ),
    },
    {
      key: "clients",
      header: t("salesReps.column.clients"),
      render: (row) => <span className="hardauto-num">{row.client_count}</span>,
    },
    {
      key: "credit",
      header: t("salesReps.column.credit"),
      render: (row) => (
        <span className="hardauto-num">{money(row.credit_followed, i18n.language)}</span>
      ),
    },
    {
      key: "account",
      header: t("salesReps.column.account"),
      render: (row) =>
        row.account ? (
          <Badge
            tone={
              row.account.status === "active"
                ? "success"
                : row.account.status === "invited"
                  ? "warning"
                  : "neutral"
            }
          >
            {t(`salesReps.access.status.${row.account.status}`)}
          </Badge>
        ) : (
          <span className="hardauto-cell__muted">{t("salesReps.access.noneShort")}</span>
        ),
    },
    {
      key: "state",
      header: t("salesReps.column.state"),
      render: (row) => (
        <StatusDot
          label={row.is_active ? t("salesReps.state.active") : t("salesReps.state.inactive")}
          tone={row.is_active ? "success" : "neutral"}
        />
      ),
    },
    ...(mayWrite
      ? [
          {
            key: "edit",
            header: "",
            render: (row: SalesRep) => (
              <Button
                size="sm"
                aria-label={t("salesReps.edit.open", { name: row.name })}
                onClick={() => setEditing(row)}
              >
                {t("salesReps.edit.button")}
              </Button>
            ),
          },
        ]
      : []),
  ];

  function save(rep: SalesRep, changes: SalesRepChanges) {
    void action.run("edit", async () => {
      try {
        await api.updateSalesRep(rep.code, {
          name: changes.name.trim(),
          territory: changes.territory.trim(),
          client_type: changes.client_type,
          qualification: changes.qualification,
          company_codes: changes.company_codes,
          is_active: changes.is_active,
        });
        setEditing(null);
        reps.reload();
      } catch {
        toast.show(t("error.save"), { tone: "danger" });
      }
    });
  }

  async function addOption(kind: SalesRepOptionKind, label: string): Promise<boolean> {
    try {
      await api.addSalesRepOption(kind, label);
      options.reload();
      return true;
    } catch (error) {
      toast.show(
        error instanceof ApiError && error.status === 409
          ? t("salesReps.lists.duplicate", { value: label })
          : t("error.save"),
        { tone: "danger" },
      );
      return false;
    }
  }

  function removeOption(option: SalesRepOption) {
    api
      .removeSalesRepOption(option.id)
      .then(() => options.reload())
      .catch((error: unknown) =>
        toast.show(
          error instanceof ApiError && error.status === 409
            ? t("salesReps.lists.inUse", { value: option.label })
            : t("error.save"),
          { tone: "danger" },
        ),
      );
  }

  function exportFile() {
    void action.run("export", async () => {
      try {
        download(await api.exportSalesReps(), "commerciaux.csv");
      } catch {
        toast.show(t("salesReps.file.exportFailed"), { tone: "danger" });
      }
    });
  }

  function importFile(file: File) {
    void action.run("import", async () => {
      try {
        const result = await api.importSalesReps(file);
        setReport(result);
        reps.reload();
      } catch (error) {
        toast.show(
          error instanceof ApiError && error.status === 422
            ? t("salesReps.file.unreadable")
            : t("error.save"),
          { tone: "danger" },
        );
      }
    });
  }

  return (
    <Page
      title={t("salesReps.title")}
      description={t("salesReps.intro")}
      actions={
        mayWrite ? (
          <>
            <Button onClick={() => setShowLists(true)}>{t("salesReps.lists.open")}</Button>
            <Button
              onClick={exportFile}
              busy={action.isBusy("export")}
              busyLabel={t("salesReps.file.export")}
            >
              {t("salesReps.file.export")}
            </Button>
            <Button
              onClick={() => fileInput.current?.click()}
              busy={action.isBusy("import")}
              busyLabel={t("salesReps.file.import")}
            >
              {t("salesReps.file.import")}
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.csv"
              hidden
              aria-label={t("salesReps.file.choose")}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) importFile(file);
              }}
            />
            <Button onClick={() => setReassigning(true)}>{t("salesReps.reassign")}</Button>
            {!creating ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t("salesReps.create")}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {creating ? (
        <form
          className="hardauto-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!name.trim()) return;
            void action.run("create", async () => {
              try {
                const created = await api.createSalesRep({
                  name: name.trim(),
                  ...(email.trim() ? { email: email.trim() } : {}),
                  territory: territory.trim(),
                  client_type: clientType,
                  qualification,
                  company_codes: companyCodes,
                });
                if (created.invitation) {
                  toast.show(
                    t(`salesReps.access.invitation.${created.invitation}`, {
                      defaultValue: created.invitation,
                    }),
                    {
                      tone:
                        created.invitation === "sent" || created.invitation === "not_needed"
                          ? "success"
                          : "warning",
                    },
                  );
                }
                setName("");
                setEmail("");
                setTerritory("");
                setClientType("");
                setQualification("");
                setCompanyCodes([]);
                setCreating(false);
                reps.reload();
                unlinked.reload();
              } catch (error) {
                const reason =
                  error instanceof ApiError && error.status === 409
                    ? typeof error.context.reason === "string"
                      ? error.context.reason
                      : ""
                    : "";
                toast.show(
                  reason
                    ? t(`salesReps.access.refused.${reason}`, { defaultValue: t("error.save") })
                    : error instanceof ApiError && error.status === 422
                      ? t("salesReps.access.badEmail")
                      : t("error.save"),
                  { tone: "danger" },
                );
              }
            });
          }}
        >
          <div className="hardauto-inline-form__field">
            <Field label={t("salesReps.newName")} required>
              {(props) => (
                <Input {...props} value={name} onChange={(event) => setName(event.target.value)} />
              )}
            </Field>
          </div>
          {mayGiveLogin ? (
            <div className="hardauto-inline-form__field">
              <Field label={t("salesReps.access.email")} hint={t("salesReps.access.emailHint")}>
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>
            </div>
          ) : null}
          <div className="hardauto-inline-form__field">
            <Field label={t("salesReps.newTerritory")}>
              {(props) => (
                <Input
                  {...props}
                  value={territory}
                  onChange={(event) => setTerritory(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("salesReps.column.clientType")}>
              {(props) => (
                <Select
                  {...props}
                  value={clientType}
                  onChange={(event) => setClientType(event.target.value)}
                >
                  <option value="">{t("salesReps.notSet")}</option>
                  {listed("client_type").map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("salesReps.column.qualification")}>
              {(props) => (
                <Select
                  {...props}
                  value={qualification}
                  onChange={(event) => setQualification(event.target.value)}
                >
                  <option value="">{t("salesReps.notSet")}</option>
                  {listed("qualification").map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <MultiSelect
              label={t("salesReps.column.companies")}
              allLabel={t("salesReps.noCompany")}
              options={(companies.data?.items ?? []).map((company) => ({
                value: company.code,
                label: company.name || company.code,
              }))}
              selected={companyCodes}
              onChange={setCompanyCodes}
              countLabel={(count) => t("salesReps.companiesChosen", { count })}
              clearLabel={t("salesReps.clear")}
              searchLabel={t("salesReps.search")}
            />
          </div>
          <p className="hardauto-cell__muted">{t("salesReps.codeHint")}</p>
          <div className="hardauto-inline-form__actions">
            <Button
              type="submit"
              variant="primary"
              busy={action.isBusy("create")}
              busyLabel={t("salesReps.save")}
            >
              {t("salesReps.save")}
            </Button>
            <Button onClick={() => setCreating(false)}>{t("salesReps.cancel")}</Button>
          </div>
        </form>
      ) : null}

      {(unlinked.data?.items ?? []).length > 0 ? (
        <Alert
          tone="warning"
          title={t("salesReps.access.unlinked", { count: unlinked.data?.items.length ?? 0 })}
        >
          {t("salesReps.access.unlinkedBody", {
            emails: (unlinked.data?.items ?? []).map((account) => account.email).join(", "),
          })}
        </Alert>
      ) : null}

      {report ? (
        <Alert
          tone={report.rejected.length ? "warning" : "success"}
          title={t("salesReps.file.report", { created: report.created, updated: report.updated })}
          action={<Button onClick={() => setReport(null)}>{t("salesReps.file.dismiss")}</Button>}
        >
          {report.rejected.length ? (
            <ul className="hardauto-rep-report" aria-label={t("salesReps.file.rejectedLabel")}>
              {report.rejected.map((line) => (
                <li key={`${line.row}-${line.reason}`}>
                  {t("salesReps.file.line", { row: line.row })}{" "}
                  {t(`salesReps.file.reason.${line.reason}`, {
                    value: line.value,
                    defaultValue: line.reason,
                  })}
                </li>
              ))}
            </ul>
          ) : null}
        </Alert>
      ) : null}

      <DataTable<SalesRep>
        caption={t("salesReps.caption")}
        columns={columns}
        rows={available}
        rowKey={(row) => row.code}
        loading={reps.loading}
        loadingLabel={t("salesReps.loading")}
        error={
          reps.failed
            ? { title: t("error.title"), onRetry: reps.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{ title: t("salesReps.empty.title"), description: t("salesReps.empty.body") }}
      />

      {editing ? (
        <SalesRepDialog
          api={api}
          rep={editing}
          options={options.data?.items ?? []}
          companies={companies.data?.items ?? []}
          busy={action.isBusy("edit")}
          mayGiveLogin={mayGiveLogin}
          mayAssign={mayAssign}
          onSave={(changes) => save(editing, changes)}
          onChanged={() => {
            reps.reload();
            unlinked.reload();
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {showLists ? (
        <ListsDialog
          options={options.data?.items ?? []}
          mayWrite={mayWrite}
          onAdd={addOption}
          onRemove={removeOption}
          onClose={() => setShowLists(false)}
        />
      ) : null}

      <Dialog
        open={reassigning}
        onClose={() => setReassigning(false)}
        title={t("salesReps.reassign")}
        closeLabel={t("salesReps.cancel")}
        footer={
          <>
            <Button onClick={() => setReassigning(false)}>{t("salesReps.cancel")}</Button>
            <Button
              variant="primary"
              busy={action.isBusy("reassign")}
              busyLabel={t("salesReps.reassignSubmit")}
              onClick={() =>
                void action.run("reassign", async () => {
                  if (!fromCode || !toCode || fromCode === toCode) return;
                  try {
                    const result = await api.reassignPortfolio(fromCode, toCode);
                    toast.show(t("salesReps.reassigned", { count: result.clients_moved }), {
                      tone: "success",
                    });
                    setReassigning(false);
                    reps.reload();
                  } catch {
                    toast.show(t("error.save"), { tone: "danger" });
                  }
                })
              }
            >
              {t("salesReps.reassignSubmit")}
            </Button>
          </>
        }
      >
        <div className="hardauto-form">
          <Field label={t("salesReps.reassignFrom")} required>
            {(props) => (
              <Select
                {...props}
                value={fromCode}
                onChange={(event) => setFromCode(event.target.value)}
              >
                <option value="">—</option>
                {available.map((rep) => (
                  <option key={rep.code} value={rep.code}>
                    {rep.name} ({rep.client_count})
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t("salesReps.reassignTo")} required>
            {(props) => (
              <Select {...props} value={toCode} onChange={(event) => setToCode(event.target.value)}>
                <option value="">—</option>
                {available
                  // A representative cannot receive their own portfolio: the
                  // server refuses it, and offering it here would be an
                  // invitation to a message rather than an action.
                  .filter((rep) => rep.code !== fromCode)
                  .map((rep) => (
                    <option key={rep.code} value={rep.code}>
                      {rep.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        </div>
      </Dialog>
    </Page>
  );
}
